import React, {useEffect, useState} from "react";
import { Box, Button, CardActions, CardContent, TextField, Typography } from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {deleteDraftTransaction, deleteTransaction, saveTransaction} from "../../store/transactionSlice";
import DateService from "../../services/DateService";
import { selectCategory } from "../../store/categorySlice";
import { selectWalletById } from "../../store/walletSlice";
import { selectCurrencies } from "../../store/currencySlice";
import { NEGATIVE_COLOR, POSITIVE_COLOR, TRANSFER_COLOR, VALUE_FONT_WEIGHT } from "../../Constants";
import ViewAndEditFormLayout from "./ViewAndEditFormLayout";
import { TransactionDetailsFields, TransactionTypeTabs } from "./TransactionFormFields";
import TransactionImagesEditor from "./TransactionImagesEditor";
import ImagePreviewModal from "./ImagePreviewModal";
import { buildImageUrl } from "../../services/ImageUploadService";
import {useTranslation} from "react-i18next";


const EditTransactionForm = ({transaction, onCancel}) => {
    const {t} = useTranslation();
    const DEFAULT_ERRORS = {
        date: null,
        time: null,
        walletFrom: null,
        walletTo: null,
        categoryTo: null,
        categoryFrom: null,
        budget: null,
        amount: null,
        amountFrom: null,
        amountTo: null,
        description: null
    };
    const dispatch = useDispatch();
    const [type, setType] = useState(transaction.type);
    const [transactionDate, setTransactionDate] = useState("");
    const [transactionTime, setTransactionTime] = useState("");
    const [description, setDescription] = useState();
    const [walletFromId, setWalletFromId] = useState();
    const [walletToId, setWalletToId] = useState();
    const [categoryToId, setCategoryToId] = useState();
    const [categoryFromId, setCategoryFromId] = useState();
    const [budgetId, setBudgetId] = useState();
    const [amount, setAmount] = useState();
    const [amountFrom, setAmountFrom] = useState();
    const [amountTo, setAmountTo] = useState();
    const [images, setImages] = useState([]);
    const [viewPreviewIdx, setViewPreviewIdx] = useState(-1);
    const [errors, setErrors] = useState(DEFAULT_ERRORS);
    const categoryTo = useSelector(selectCategory(categoryToId));
    const categoryFrom = useSelector(selectCategory(categoryFromId));
    const walletFrom = useSelector((state) => selectWalletById(state, walletFromId));
    const walletTo = useSelector((state) => selectWalletById(state, walletToId));
    const currencies = useSelector(selectCurrencies) || [];
    const currencyFrom = walletFrom?.currency != null
        ? currencies.find((c) => c.id === walletFrom.currency || c.id == walletFrom.currency)
        : null;
    const currencyTo = walletTo?.currency != null
        ? currencies.find((c) => c.id === walletTo.currency || c.id == walletTo.currency)
        : null;
    const sameTransferCurrency =
        type === "TRANSFER" &&
        walletFrom?.currency != null &&
        walletTo?.currency != null &&
        walletFrom.currency === walletTo.currency;
    const isDraft = transaction?.state === "DRAFT";

    const getTypeLabel = (value) => {
        switch (value) {
            case "EXPENSE":
                return t("common.transactionType.expense");
            case "INCOME":
                return t("common.transactionType.income");
            case "TRANSFER":
                return t("common.transactionType.transfer");
            case "SET_BALANCE":
                return t("common.transactionType.setBalance");
            case "BUDGET":
            case "CHANGE_BUDGET":
                return t("common.transactionType.changeBudget");
            default:
                return value;
        }
    };


    useEffect(() => {
        setDescription(transaction.description);
        setAmount(transaction.valueFrom);
        setAmountFrom(transaction.valueFrom);
        setAmountTo(transaction.valueTo);
        setType(transaction.type);
        if(transaction.type === 'EXPENSE') {
            setWalletFromId(transaction.walletFrom);
            setCategoryToId(transaction.categoryTo);
            setBudgetId(transaction.budget)
        }
        if(transaction.type === 'INCOME' || transaction.type ==='TRANSFER') {
            setWalletToId(transaction.walletTo);
            setCategoryFromId(transaction.categoryFrom);

        }
        if(transaction.type ==='TRANSFER') {
            setWalletFromId(transaction.walletFrom);
            setWalletToId(transaction.walletTo);
        }

        const dateTime = DateService.getDate(transaction.date);
        // Format date as YYYY-MM-DD
        const year = dateTime.getFullYear();
        const month = String(dateTime.getMonth() + 1).padStart(2, '0');
        const day = String(dateTime.getDate()).padStart(2, '0');
        setTransactionDate(`${year}-${month}-${day}`);

        // Format time as HH:MM
        const hours = String(dateTime.getHours()).padStart(2, '0');
        const minutes = String(dateTime.getMinutes()).padStart(2, '0');
        setTransactionTime(`${hours}:${minutes}`);
        // `transaction.images` is the new server field (id+guid+fileName); fall back to
        // legacy `imageList` (ids only — fileName left blank, server URL still works).
        if (Array.isArray(transaction.images) && transaction.images.length > 0) {
            setImages(transaction.images.map((img) => ({
                id: img.id,
                guid: img.guid,
                fileName: img.fileName,
            })));
        } else if (Array.isArray(transaction.imageList) && transaction.imageList.length > 0) {
            setImages(transaction.imageList.map((id) => ({id, guid: null, fileName: null})));
        } else {
            setImages([]);
        }
        setErrors(DEFAULT_ERRORS);
    }, [transaction]);

    // useEffect(() => {
    //     dispatch(fetchCurrencies())
    // }, []);

 

    const onDelete = () => {
        if (transaction?.id) {
            dispatch(deleteTransaction(transaction, () => onCancel()));
        } else {
            dispatch(deleteDraftTransaction(transaction, () => onCancel()));
        }
    };

    const saveWithState = (targetState) => {
        if (!validateForm()) {
            return;
        }
        const transactionToSave = {
            ...transaction, description: description,
            type: type,
            state: targetState,
            date: transactionDate + 'T' + transactionTime + ':00',
            walletFrom: walletFromId, walletTo: walletToId,
            categoryTo: categoryToId, categoryFrom: categoryFromId,
            valueFrom: amountFrom ?? amount,
            valueTo: amountTo ?? amount,
            images: images.map((img) => ({id: img.id, guid: img.guid, fileName: img.fileName})),
            imageList: images.map((img) => img.id),
        }
        if(type === 'EXPENSE') {
            transactionToSave.walletFrom = walletFromId;
            transactionToSave.walletTo = null;
            transactionToSave.categoryFrom = null;
            transactionToSave.categoryTo = categoryToId;
            transactionToSave.budget = budgetId;
            transactionToSave.valueFrom = amount;
            transactionToSave.valueTo = amount;
        }
        if(type === 'INCOME') {
            transactionToSave.walletFrom = null;
            transactionToSave.walletTo = walletToId;
            transactionToSave.categoryFrom = categoryFromId;
            transactionToSave.categoryTo = null;
            transactionToSave.valueFrom = amount;
            transactionToSave.valueTo = amount;
        }
        if(type === 'TRANSFER') {
            transactionToSave.walletFrom = walletFromId;
            transactionToSave.walletTo = walletToId;
            transactionToSave.categoryFrom = null;
            transactionToSave.categoryTo = null;
            if (sameTransferCurrency) {
                const unified = amount ?? amountFrom ?? amountTo;
                transactionToSave.valueFrom = unified;
                transactionToSave.valueTo = unified;
            } else {
                transactionToSave.valueFrom = amountFrom ?? amount ?? amountTo;
                transactionToSave.valueTo = amountTo ?? amountFrom ?? amount;
            }
        }
        return new Promise((resolve) => {
            dispatch(saveTransaction(
                transactionToSave,
                () => resolve("close"),
                (warning) => window.alert(warning)
            ));
        });
    };

    const handleSave = () => saveWithState(isDraft ? "DRAFT" : (transaction?.state || "ACTIVE"));
    const handleApply = () => saveWithState("ACTIVE");
    const handleSaveAsDraft = () => saveWithState("DRAFT");
    const runAndClose = (action) => {
        const result = action();
        if (result && typeof result.then === "function") {
            result.then((saveAction) => {
                if (saveAction === "close" && onCancel) {
                    onCancel();
                }
            });
        }
    };

    const validateForm = () => {
        const nextErrors = {...DEFAULT_ERRORS};
        if (!transactionDate) {
            nextErrors.date = "Date is required";
        }
        if (!transactionTime) {
            nextErrors.time = "Time is required";
        }
        if (type === "EXPENSE") {
            if (!walletFromId) nextErrors.walletFrom = "Wallet is required";
            if (!categoryToId) nextErrors.categoryTo = "Category is required";
            if (amount === null || amount === undefined || `${amount}`.trim() === "") {
                nextErrors.amount = "Amount is required";
            }
        } else if (type === "INCOME") {
            if (!walletToId) nextErrors.walletTo = "Wallet is required";
            if (!categoryFromId) nextErrors.categoryFrom = "Category is required";
            if (amount === null || amount === undefined || `${amount}`.trim() === "") {
                nextErrors.amount = "Amount is required";
            }
        } else if (type === "TRANSFER") {
            if (!walletFromId) nextErrors.walletFrom = "Source wallet is required";
            if (!walletToId) nextErrors.walletTo = "Target wallet is required";
            if (walletFromId && walletToId && walletFromId === walletToId) {
                nextErrors.walletTo = "Wallets must be different";
            }
            if (sameTransferCurrency) {
                const unified = amount ?? amountFrom ?? amountTo;
                if (unified === null || unified === undefined || `${unified}`.trim() === "") {
                    nextErrors.amount = "Amount is required";
                }
            } else {
                if (amountFrom === null || amountFrom === undefined || `${amountFrom}`.trim() === "") {
                    nextErrors.amountFrom = "Source amount is required";
                }
                if (amountTo === null || amountTo === undefined || `${amountTo}`.trim() === "") {
                    nextErrors.amountTo = "Target amount is required";
                }
            }
        }
        setErrors(nextErrors);
        return !Object.values(nextErrors).some(Boolean);
    };

    const viewContent = (
        <>
            <CardContent>
                <Box sx={{ m: 2 }}>
                <Typography variant="body2" color="text.secondary">{t("common.labels.date")}</Typography>
                <Typography variant="body1">{transactionDate} {transactionTime}</Typography>
            </Box>
            <Box sx={{ m: 2 }}>
                <Typography variant="body2" color="text.secondary">{t("common.labels.type")}</Typography>
                <Typography variant="body1">{getTypeLabel(type)}</Typography>
            </Box>
            {(type === "EXPENSE" || type === "TRANSFER") && (
                <Box sx={{ m: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t("common.labels.walletFrom")}</Typography>
                    <Typography variant="body1">{walletFrom?.name || (walletFromId != null ? String(walletFromId) : "—")}</Typography>
                </Box>
            )}
            {(type === "INCOME" || type === "TRANSFER") && (
                <Box sx={{ m: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t("common.labels.walletTo")}</Typography>
                    <Typography variant="body1">{walletTo?.name || (walletToId != null ? String(walletToId) : "—")}</Typography>
                </Box>
            )}
            {(type === "EXPENSE" || type === "INCOME") && (
                <Box sx={{ m: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t("common.labels.category")}</Typography>
                    <Typography variant="body1">
                        {type === "EXPENSE"
                            ? (categoryTo?.name || (categoryToId != null ? String(categoryToId) : "—"))
                            : (categoryFrom?.name || (categoryFromId != null ? String(categoryFromId) : "—"))}
                    </Typography>
                </Box>
            )}
            <Box sx={{ m: 2 }}>
                <Typography variant="body2" color="text.secondary">{t("common.labels.amount")}</Typography>
                {type === "TRANSFER" ? (
                    <>
                        {sameTransferCurrency ? (
                            <Typography variant="body1" sx={{ color: TRANSFER_COLOR, fontWeight: VALUE_FONT_WEIGHT }}>
                                {`${amount ?? amountFrom ?? amountTo ?? "—"}${currencyFrom?.shortName ? " " + currencyFrom.shortName : ""}`}
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="body1" sx={{ color: NEGATIVE_COLOR, fontWeight: VALUE_FONT_WEIGHT }}>
                                    −{amountFrom ?? "—"}{currencyFrom?.shortName ? " " + currencyFrom.shortName : ""}
                                </Typography>
                                <Typography variant="body1" sx={{ color: POSITIVE_COLOR, fontWeight: VALUE_FONT_WEIGHT }}>
                                    +{amountTo ?? "—"}{currencyTo?.shortName ? " " + currencyTo.shortName : ""}
                                </Typography>
                            </>
                        )}
                    </>
                ) : (
                    <Typography
                        variant="body1"
                        sx={{
                            color: type === "EXPENSE" ? NEGATIVE_COLOR : POSITIVE_COLOR,
                            fontWeight: VALUE_FONT_WEIGHT
                        }}
                    >
                        {(type === "EXPENSE" ? "−" : "+")}{amount ?? "—"}{currencyFrom?.shortName ? " " + currencyFrom.shortName : ""}
                    </Typography>
                )}
            </Box>
            <Box sx={{ m: 2 }}>
                <Typography variant="body2" color="text.secondary">{t("common.labels.description")}</Typography>
                <Typography variant="body1">{description || "—"}</Typography>
            </Box>
            {images && images.length > 0 && (
                <Box sx={{ m: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t("forms.transaction.images.sectionTitle")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1 }}>
                        {images.map((image, idx) => (
                            <Box
                                key={`view_img_${image.id}`}
                                component="img"
                                src={buildImageUrl(image.id, image.fileName)}
                                alt={image.fileName || ""}
                                onClick={() => setViewPreviewIdx(idx)}
                                sx={{
                                    width: 96,
                                    height: 96,
                                    objectFit: "cover",
                                    borderRadius: "12px",
                                    border: "1px solid rgba(0,0,0,0.12)",
                                    cursor: "pointer",
                                    flex: "0 0 96px",
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}
            </CardContent>
            <CardActions sx={{ ml: "2rem", mb: "1rem", mt: "1rem", mr: "2rem", height: "4rem" }}>
                <Box sx={{ position: "relative", width: "100%" }}>
                    <Box sx={{ position: "absolute", left: 0 }}>
                        <Button variant="outlined" sx={{ mr: "1rem" }} onClick={onCancel}>{t("common.buttons.close")}</Button>
                    </Box>
                </Box>
            </CardActions>
        </>
    );

    const editContent = ({ onCancel: handleCancel, onSave: handleSaveLayout }) => (
        <>
            <CardContent sx={{ minHeight: 200, overflowY: "auto", maxHeight: "65%" }}>
                <TransactionTypeTabs type={type} onChange={setType} ariaLabel={t("forms.transaction.typeTabsAria")} />
                <Box sx={{ m: 2 }}>
                    <TextField
                        id="date"
                        label={t("common.labels.date")}
                        type="date"
                        value={transactionDate}
                        onChange={e => {
                            setTransactionDate(e.target.value);
                            setErrors(prev => ({ ...prev, date: null }));
                        }}
                        error={!!errors.date}
                        helperText={errors.date}
                        sx={{ width: "50%" }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <TextField
                        id="time"
                        label={t("common.labels.time")}
                        type="time"
                        value={transactionTime}
                        onChange={e => {
                            setTransactionTime(e.target.value);
                            setErrors(prev => ({ ...prev, time: null }));
                        }}
                        error={!!errors.time}
                        helperText={errors.time}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            step: 300, // 5 min
                        }}
                        sx={{ width: "45%", marginLeft: "12px" }}
                    />
                </Box>
                <TransactionDetailsFields
                    type={type}
                    walletFromId={walletFromId}
                    walletToId={walletToId}
                    categoryToId={categoryToId}
                    categoryFromId={categoryFromId}
                    budgetId={budgetId}
                    amountValue={amount ?? amountFrom ?? amountTo}
                    transferAmountFromValue={amountFrom}
                    transferAmountToValue={amountTo}
                    description={description}
                    onWalletFromChange={(walletId) => {
                        setWalletFromId(walletId);
                        setErrors(prev => ({ ...prev, walletFrom: null }));
                    }}
                    onWalletToChange={(walletId) => {
                        setWalletToId(walletId);
                        setErrors(prev => ({ ...prev, walletTo: null }));
                    }}
                    onCategoryToChange={(categoryId) => {
                        setCategoryToId(categoryId);
                        setErrors(prev => ({ ...prev, categoryTo: null }));
                    }}
                    onCategoryFromChange={(categoryId) => {
                        setCategoryFromId(categoryId);
                        setErrors(prev => ({ ...prev, categoryFrom: null }));
                    }}
                    onBudgetChange={(nextBudgetId) => setBudgetId(nextBudgetId)}
                    onAmountChange={(val) => {
                        setAmount(val);
                        setAmountFrom(val);
                        setAmountTo(val);
                        setErrors(prev => ({ ...prev, amount: null }));
                    }}
                    onTransferAmountFromChange={(val) => {
                        setAmountFrom(val);
                        setErrors(prev => ({ ...prev, amountFrom: null }));
                    }}
                    onTransferAmountToChange={(val) => {
                        setAmountTo(val);
                        setErrors(prev => ({ ...prev, amountTo: null }));
                    }}
                    onDescriptionChange={(val) => setDescription(val)}
                    sameTransferCurrency={sameTransferCurrency}
                    fieldErrors={errors}
                />
                <TransactionImagesEditor value={images} onChange={setImages} />
            </CardContent>
            <CardActions sx={{ ml: "2rem", mb: "1rem", mt: "1rem", mr: "2rem", height: "4rem" }}>
                <Box sx={{ position: "relative", width: "100%" }}>
                    <Box sx={{ position: "absolute", left: 0 }}>
                        <Button variant="outlined" sx={{ mr: "1rem" }} onClick={handleCancel}>{t("common.buttons.cancel")}</Button>
                        {isDraft ? (
                            <>
                                <Button variant="contained" sx={{ mr: "1rem" }} onClick={() => runAndClose(handleApply)}>
                                    {t("common.buttons.apply")}
                                </Button>
                                <Button variant="outlined" onClick={() => runAndClose(handleSaveAsDraft)}>
                                    Save as draft
                                </Button>
                            </>
                        ) : (
                            <Button variant="contained" onClick={handleSaveLayout}>{t("common.buttons.save")}</Button>
                        )}
                    </Box>
                </Box>
            </CardActions>
        </>
    );

    return (
        <>
            <ViewAndEditFormLayout
                editTitle={transaction.id ? t("forms.transaction.editTitle") : t("forms.transaction.createTitle")}
                viewTitle={t("forms.transaction.viewTitle")}
                initialEdit={!transaction.id || isDraft}
                onCancel={onCancel}
                onSave={handleSave}
                onDelete={(transaction.id || isDraft) ? onDelete : undefined}
                showDeleteInEdit={true}
                viewContent={transaction.id ? viewContent : null}
                editContent={editContent}
            />
            <ImagePreviewModal
                open={viewPreviewIdx >= 0}
                src={viewPreviewIdx >= 0 ? buildImageUrl(images[viewPreviewIdx]?.id, images[viewPreviewIdx]?.fileName) : null}
                title={viewPreviewIdx >= 0 ? (images[viewPreviewIdx]?.fileName || "") : ""}
                onClose={() => setViewPreviewIdx(-1)}
            />
        </>
    );
}

export default EditTransactionForm